import { useScrollAnimation } from '@/hooks/useScrollAnimation';

function RevenueChart() {
  return (
    <div data-testid="revenue-recovery-chart" className="w-full max-w-md mx-auto">
      <svg viewBox="0 0 400 220" className="w-full" aria-label="Revenue recovery chart showing 20% increase">
        {/* Grid lines */}
        <line x1="60" y1="20" x2="60" y2="180" stroke="#D8E2DC" strokeWidth="1" />
        <line x1="60" y1="180" x2="380" y2="180" stroke="#D8E2DC" strokeWidth="1" />
        <line x1="60" y1="100" x2="380" y2="100" stroke="#D8E2DC" strokeWidth="0.5" strokeDasharray="4" />
        <line x1="60" y1="140" x2="380" y2="140" stroke="#D8E2DC" strokeWidth="0.5" strokeDasharray="4" />
        <line x1="60" y1="60" x2="380" y2="60" stroke="#D8E2DC" strokeWidth="0.5" strokeDasharray="4" />

        {/* Y-axis labels */}
        <text x="50" y="184" textAnchor="end" className="font-body" fill="#2D3A3A" opacity="0.5" fontSize="10">$0</text>
        <text x="50" y="144" textAnchor="end" className="font-body" fill="#2D3A3A" opacity="0.5" fontSize="10">$25K</text>
        <text x="50" y="104" textAnchor="end" className="font-body" fill="#2D3A3A" opacity="0.5" fontSize="10">$50K</text>
        <text x="50" y="64" textAnchor="end" className="font-body" fill="#2D3A3A" opacity="0.5" fontSize="10">$75K</text>

        {/* Before bars */}
        <rect x="90" y="100" width="40" height="80" fill="#D8E2DC" rx="2" className="chart-bar" style={{ animationDelay: '0.2s' }} />
        <rect x="150" y="110" width="40" height="70" fill="#D8E2DC" rx="2" className="chart-bar" style={{ animationDelay: '0.3s' }} />
        <rect x="210" y="95" width="40" height="85" fill="#D8E2DC" rx="2" className="chart-bar" style={{ animationDelay: '0.4s' }} />

        {/* After bars (with VitalsFlow) */}
        <rect x="270" y="60" width="40" height="120" fill="#B9E6D5" rx="2" className="chart-bar" style={{ animationDelay: '0.5s' }} />
        <rect x="330" y="45" width="40" height="135" fill="#1B4332" rx="2" className="chart-bar" style={{ animationDelay: '0.6s' }} />

        {/* +20% annotation */}
        <line x1="310" y1="38" x2="370" y2="38" stroke="#1B4332" strokeWidth="1.5" />
        <text x="340" y="30" textAnchor="middle" fill="#1B4332" fontSize="12" fontWeight="700" className="font-body">+20%</text>

        {/* X-axis labels */}
        <text x="110" y="200" textAnchor="middle" fill="#2D3A3A" opacity="0.5" fontSize="10" className="font-body">Q1</text>
        <text x="170" y="200" textAnchor="middle" fill="#2D3A3A" opacity="0.5" fontSize="10" className="font-body">Q2</text>
        <text x="230" y="200" textAnchor="middle" fill="#2D3A3A" opacity="0.5" fontSize="10" className="font-body">Q3</text>
        <text x="290" y="200" textAnchor="middle" fill="#B9E6D5" opacity="0.8" fontSize="10" fontWeight="600" className="font-body">Q4</text>
        <text x="350" y="200" textAnchor="middle" fill="#1B4332" fontSize="10" fontWeight="600" className="font-body">Q5</text>

        {/* Legend */}
        <rect x="100" y="212" width="10" height="6" fill="#D8E2DC" rx="1" />
        <text x="116" y="218" fill="#2D3A3A" opacity="0.5" fontSize="9" className="font-body">Before</text>
        <rect x="170" y="212" width="10" height="6" fill="#1B4332" rx="1" />
        <text x="186" y="218" fill="#2D3A3A" opacity="0.7" fontSize="9" className="font-body">With VitalsFlow</text>
      </svg>
    </div>
  );
}

const painPoints = [
  {
    stat: '50%',
    title: 'of physicians report burnout',
    desc: '2 hours of paperwork for every 1 hour with patients.',
  },
  {
    stat: '$100K',
    title: 'lost per provider per year',
    desc: 'In unclaimed billing revenue due to coding errors.',
  },
  {
    stat: '44%',
    title: 'of rural hospitals at a deficit',
    desc: 'Administrative overhead is the #1 preventable cause.',
  },
];

export default function FinancialMoat() {
  const [ref, isVisible] = useScrollAnimation(0.1);
  const [refChart, isChartVisible] = useScrollAnimation(0.2);

  return (
    <section data-testid="financial-moat-section" className="py-24 md:py-32 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Section heading */}
        <div ref={ref} className={`max-w-3xl mb-16 scroll-fade-up ${isVisible ? 'visible' : ''}`}>
          <p className="uppercase text-xs tracking-[0.2em] text-vf-forest font-body font-semibold mb-4">The Problem</p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-vf-charcoal tracking-tight mb-6 leading-tight">
            Stop Leaving 20% of Your Revenue on the Table.
          </h2>
          <p className="font-body text-base md:text-lg text-vf-charcoal/70 leading-relaxed">
            The average rural clinic loses $50k–$100k per physician every year due to billing errors
            and administrative burnout. VitalsFlow AI closes the loop between the patient visit and
            the insurance claim.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Pain point cards */}
          <div className={`stagger-children ${isVisible ? 'visible' : ''} space-y-6`}>
            {painPoints.map((point) => (
              <div
                key={point.stat}
                className="bg-white border border-vf-sage p-6 md:p-8 hover:-translate-y-1 hover:shadow-sm transition-all duration-300"
              >
                <div className="flex items-start gap-5">
                  <span className="font-heading font-bold text-3xl md:text-4xl text-vf-forest leading-none shrink-0">
                    {point.stat}
                  </span>
                  <div>
                    <h3 className="font-body font-semibold text-base text-vf-charcoal mb-1">
                      {point.title}
                    </h3>
                    <p className="font-body text-sm text-vf-charcoal/60">{point.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Revenue chart */}
          <div ref={refChart} className={`scroll-fade-up ${isChartVisible ? 'visible' : ''}`}>
            <div className="bg-white border border-vf-sage p-6 md:p-8">
              <p className="uppercase text-xs tracking-[0.2em] text-vf-forest/70 font-body font-semibold mb-4">
                Revenue Recovery
              </p>
              <h3 className="font-heading font-semibold text-xl text-vf-charcoal mb-6">
                Average Billable Revenue Per Provider
              </h3>
              <RevenueChart />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
